/**
 * ProductDescriptionSection Component
 *
 * Displays all 7 product fields in two sections with save functionality:
 * - Product Details & Setup: Product Link, Description, Platform
 * - Previous User Step & Design Information: Previous Step, Tone, Design, Category
 *
 * Sprint 5: Added footer toolbar with TasksTable, Audience Count input,
 * and action buttons (Create Audiences, Update Interview Questions, Save)
 *
 * @module product
 * @requirements Sprint 2: FR-001 to FR-004, FR-025 to FR-028
 * @requirements Sprint 3: FR-005 to FR-010, FR-019 to FR-024
 * @requirements Sprint 4: FR-011 to FR-013 (API integration for fetch-from-link)
 * @requirements Sprint 5: FR-014 to FR-018 (footer toolbar with tasks)
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Stack, Typography, Divider, Button, CircularProgress, Alert, Snackbar, Box, Autocomplete, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tabs, Tab, Paper } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { StyledTextField } from '@modules/ui-components/forms/fields/StyledTextField';
import { StyledTextArea } from '@modules/ui-components/forms/fields/StyledTextArea';
import { StyledSelectDropdown } from '@modules/ui-components/forms/fields/StyledSelectDropdown';
import type { SelectOption } from '@modules/ui-components/forms/fields/StyledSelectDropdown';
import { useChangeDetection } from '@modules/ui-components/hooks/useChangeDetection';
import { WithGuideHelp } from '@modules/ui-components/dialogs/WithGuideHelp';
import { SAccordion, SAccordionNestingContext } from '@modules/ui-components/dialogs/SAccordion';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import {
  AvailableModelSelector,
  PROJECT_DEFAULT_AI_MODEL,
  useProjectDefaultAIModel,
} from '@modules/ai-settings';
import { ProjectThemeSection } from '@modules/themes/components/ProjectThemeSection';
import { useProjectThemes } from '@modules/themes/hooks/useProjectThemes';
import { ProjectService } from '@modules/firebase-core/services/ProjectService';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { useNotification } from '../contexts/NotificationContext';
import type { Project, Task } from '@segmently/schema-models/entities';
import { fetchProductDescriptionFromUrlWithProxy } from '../services/apiService';
import { TasksTable } from './TasksTable';
import { CreateAudiencesDialog } from './CreateAudiencesDialog';
import { VariableSetEditor } from './VariableSetEditor';
import { ScrapeAnalyzeSection } from './ScrapeAnalyzeSection';
import { TrafficChannelsEditor } from './TrafficChannelsEditor';
import type { TrafficChannel } from './TrafficChannelsEditor';
import { ScrapeReviewPanel } from './ScrapeReviewPanel';
import { ResearchHistoryDialog } from './ResearchHistoryDialog';
import { PRODUCT_VARIABLES } from '@test-kit/selectors/variable-privacy';
import { AUDIENCE_GENERATION } from '@test-kit/selectors/audiences';

// ============================================================================
// TYPESCRIPT INTERFACES (FR-025, FR-026)
// ============================================================================

/**
 * User object interface for current user
 */
interface User {
  uid: string;
  email?: string;
}

/**
 * Props for ProductDescriptionSection component
 * Extended in Sprint 3 with projectId, currentUser, and callbacks
 * Extended in Sprint 5 with tasks and selectedTaskIds for footer toolbar
 */
export interface ProductDescriptionSectionProps {
  /** Project data to display in form fields */
  project: Project | null;
  /** Project ID for save operations (FR-005) */
  projectId?: string;
  /** Current user for tracking who made updates (FR-005) */
  currentUser?: User;
  /** Callback invoked after successful save (FR-022) */
  onDescriptionAndSettingsSaved?: () => void;
  /** Whether description is effectively saved (FR-019) - can be passed from parent */
  isDescriptionEffectivelySaved?: boolean;
  /** Callback invoked after audience generation (FR-023) */
  onAudiencesGenerated?: () => void;
  /** Callback to refresh project data from parent (FR-024) */
  fetchProjectData?: () => void | Promise<void>;
  /** Callback to clear project task status */
  clearProjectTaskStatus?: () => void;
  // ============================================================================
  // SPRINT 5: FR-014 to FR-018 Props
  // ============================================================================
  /** Array of tasks for the project - footer toolbar appears when tasks.length > 0 (FR-014) */
  tasks?: Task[];
  /** Selected task IDs for footer toolbar - if provided, used instead of internal state (FR-017) */
  selectedTaskIds?: string[];
  /** Callback when task selection changes */
  onTaskSelectionChange?: (selectedTaskIds: string[]) => void;
  /** Callback for Create Audiences button (FR-015) */
  onCreateAudiences?: (
    audienceCount: number,
    selectedTaskIds: string[],
    useDeepResearch?: boolean,
    model?: string,
  ) => Promise<void>;
  /** Callback for Update Interview Questions button (FR-015) */
  onUpdateInterviewQuestions?: (model?: string) => Promise<void>;
  /** Callback for Adapt Questions button — AI adapts questions to product context */
  onAdaptQuestions?: () => Promise<void>;
  /** Callback for Delete button - deletes selected tasks (shown in TasksTable toolbar) */
  onDeleteTasks?: (selectedTaskIds: string[]) => Promise<void>;
  // ============================================================================
  // SPRINT 6: TasksTable additional props
  // ============================================================================
  /** Callback for Create Tasks from Description button */
  onCreateTasksFromDescription?: () => Promise<void>;
  /** Callback for creating a new task */
  onCreateTask?: (taskData: Partial<Task>) => Promise<void>;
  /** Callback for updating an existing task */
  onUpdateTask?: (taskId: string, taskData: Partial<Task>) => Promise<void>;
  /** Callback for deleting a single task */
  onDeleteTask?: (taskId: string) => Promise<void>;
  /** Callback for duplicating a task */
  onDuplicateTask?: (task: Task) => Promise<void>;
  /** Callback for generating audience for a task */
  onGenerateAudience?: (task: Task) => Promise<void>;
  // ============================================================================
  // VARIABLES: Project-level variable definitions
  // ============================================================================
  /** Project-level variables (replaces Task.requiredInfo with structured definitions) */
  variables?: Record<string, import('./VariableSetEditor').EditorVariable>;
  /** Callback when variables are updated */
  onVariablesChange?: import('@modules/product/components/VariableSetEditor').VariableChangeHandler;
  /** Callback for saving a variable version snapshot */
  onSaveVariableVersion?: (description: string) => void | Promise<void>;
  /** Current variable version number */
  currentVariableVersion?: number;
}

/**
 * Form data structure for all 7 product fields
 */
interface ProductFormData {
  url: string;
  description: string;
  platform: string;
  previousUserStepDescription: string;
  toneOfVoice: string;
  designDescription: string;
  category: string;
  trafficChannels: TrafficChannel[];
  targetCountries: string[];
  /** B2B polymorphism toggle: 'b2c' (default) or 'b2b' (ICP archetypes + DMU packs). */
  audienceModel: 'b2c' | 'b2b';
}

// ============================================================================
// VALIDATION CONSTANTS AND FUNCTIONS (FR-007, FR-008)
// ============================================================================

/** Country options for target geo selector (ISO 3166-1 alpha-2) */
interface CountryOption { code: string; label: string }
const COUNTRY_OPTIONS: CountryOption[] = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'ES', label: 'Spain' },
  { code: 'IT', label: 'Italy' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'SE', label: 'Sweden' },
  { code: 'NO', label: 'Norway' },
  { code: 'DK', label: 'Denmark' },
  { code: 'FI', label: 'Finland' },
  { code: 'PL', label: 'Poland' },
  { code: 'PT', label: 'Portugal' },
  { code: 'AT', label: 'Austria' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'BE', label: 'Belgium' },
  { code: 'IE', label: 'Ireland' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'JP', label: 'Japan' },
  { code: 'KR', label: 'South Korea' },
  { code: 'CN', label: 'China' },
  { code: 'IN', label: 'India' },
  { code: 'BR', label: 'Brazil' },
  { code: 'MX', label: 'Mexico' },
  { code: 'AR', label: 'Argentina' },
  { code: 'CL', label: 'Chile' },
  { code: 'CO', label: 'Colombia' },
  { code: 'IL', label: 'Israel' },
  { code: 'AE', label: 'UAE' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'TR', label: 'Turkey' },
  { code: 'RU', label: 'Russia' },
  { code: 'UA', label: 'Ukraine' },
  { code: 'ZA', label: 'South Africa' },
  { code: 'NG', label: 'Nigeria' },
  { code: 'EG', label: 'Egypt' },
  { code: 'SG', label: 'Singapore' },
  { code: 'MY', label: 'Malaysia' },
  { code: 'TH', label: 'Thailand' },
  { code: 'ID', label: 'Indonesia' },
  { code: 'PH', label: 'Philippines' },
  { code: 'VN', label: 'Vietnam' },
  { code: 'TW', label: 'Taiwan' },
  { code: 'HK', label: 'Hong Kong' },
];

/** URL validation regex - must start with http:// or https:// */
const URL_VALIDATION_REGEX = /^https?:\/\/.*/;

/** Error message for invalid URL */
const ERROR_INVALID_URL = 'Please enter a valid URL (must start with http:// or https://)';

/** Error message for empty description */
const ERROR_EMPTY_DESCRIPTION = 'Description cannot be empty';

/**
 * Validates Product Link URL format
 * @param url - Product link URL (optional)
 * @returns Error message if invalid, null if valid
 */
function validateProductLink(url: string | null | undefined): string | null {
  // Empty URL is valid (optional field)
  if (!url || url.trim() === '') {
    return null;
  }

  // URL must match http:// or https:// pattern
  if (!URL_VALIDATION_REGEX.test(url)) {
    return ERROR_INVALID_URL;
  }

  return null;
}

/**
 * Validates Product Description is not empty
 * @param description - Product description (required)
 * @returns Error message if invalid, null if valid
 */
function validateProductDescription(description: string | null | undefined): string | null {
  if (!description || description.trim() === '') {
    return ERROR_EMPTY_DESCRIPTION;
  }
  return null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Platform options for dropdown (FR-001 constraint: Web/iOS/Android) */
const PLATFORM_OPTIONS: SelectOption[] = [
  { value: 'web', label: 'Web' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

/** Audience model toggle (B2C vs B2B) — drives audience/actor/interview/analysis prompts. */
const AUDIENCE_MODEL_OPTIONS: SelectOption[] = [
  { value: 'b2c', label: 'B2C — individual user personas' },
  { value: 'b2b', label: 'B2B — ICP archetypes with DMU packs' },
];

// ============================================================================
// SPRINT 5: FOOTER TOOLBAR CONSTANTS (FR-015)
// ============================================================================

/** Audience Count default value - passed to CreateAudiencesDialog */
const AUDIENCE_COUNT_DEFAULT = 4;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ProductDescriptionSection Component
 *
 * Displays all 7 product fields in two sections with save functionality:
 * - Product Details & Setup: Product Link, Description, Platform
 * - Previous User Step & Design Information: Previous Step, Tone, Design, Category
 *
 * @param props - Component props
 * @returns React component
 */
export const ProductDescriptionSection: React.FC<ProductDescriptionSectionProps> = ({
  project,
  projectId,
  currentUser,
  onDescriptionAndSettingsSaved,
  isDescriptionEffectivelySaved: _externalSavedState, // Reserved for future parent-controlled state
  onAudiencesGenerated,
  fetchProjectData,
  clearProjectTaskStatus: _clearProjectTaskStatus, // Reserved for future task status clearing
  // Sprint 5 props (FR-014 to FR-018)
  tasks = [],
  selectedTaskIds: externalSelectedTaskIds,
  onTaskSelectionChange,
  onCreateAudiences,
  onUpdateInterviewQuestions,
  onAdaptQuestions,
  // Sprint 7 props - task deletion (moved from footer to TasksTable toolbar)
  onDeleteTasks,
  // Sprint 6 props - TasksTable callbacks
  onCreateTasksFromDescription,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onDuplicateTask,
  onGenerateAudience,
  // Variables
  variables: externalVariables = {},
  onVariablesChange,
  onSaveVariableVersion,
  currentVariableVersion,
}) => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const projectDefaultModel = useProjectDefaultAIModel(projectId);

  // Project themes — V2 theme management
  const projectThemes = useProjectThemes(projectId ?? '');

  // The owning view refreshes these values after persistence, including same-id edits.
  const variables = externalVariables;
  const handleVariablesChange = useCallback<import('@modules/product/components/VariableSetEditor').VariableChangeHandler>((next, reviewed) => {
    if (!onVariablesChange) throw new Error('Variable saving is unavailable.');
    return onVariablesChange(next, reviewed);
  }, [onVariablesChange]);

  // ============================================================================
  // STATE MANAGEMENT (FR-019, FR-020)
  // ============================================================================

  // Form data state - all 7 fields
  const [formData, setFormData] = useState<ProductFormData>({
    url: '',
    description: '',
    platform: '',
    previousUserStepDescription: '',
    toneOfVoice: '',
    designDescription: '',
    category: '',
    trafficChannels: [],
    targetCountries: [],
    audienceModel: 'b2c',
  });

  // Validation error states
  const [urlError, setUrlError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  // Loading states for each operation (FR-020)
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [isFetchingLinkDescription, setIsFetchingLinkDescription] = useState(false); // FR-012: Loading state for fetch-from-link
  const [isUpdatingQuestions, setIsUpdatingQuestions] = useState(false); // FR-016: Loading state for Update Interview Questions
  const [isAdaptingQuestions, setIsAdaptingQuestions] = useState(false);
  // Admin / debug: read-only view of currently-saved adapted questions on the project.
  // Two modes:
  //   - flat (B2C / legacy): shows project.adoptedQuestions string in a single pre-block
  //   - role-blocks (B2B): tabbed view of project.adoptedQuestionsBlocks (one tab per block key)
  const [viewQuestionsOpen, setViewQuestionsOpen] = useState(false);
  const [viewQuestionsTab, setViewQuestionsTab] = useState(0);
  const [isCreatingAudiences, setIsCreatingAudiences] = useState(false); // FR-016: Loading state for Create Audiences
  const [reviewAnalysisId, setReviewAnalysisId] = useState<string | null>(null); // Scrape review panel
  const [showResearchHistory, setShowResearchHistory] = useState(false); // Research history dialog

  // Dialog states
  const [createAudiencesDialogOpen, setCreateAudiencesDialogOpen] = useState(false);
  const [updateQuestionsDialogOpen, setUpdateQuestionsDialogOpen] = useState(false);
  const [questionAdaptationModel, setQuestionAdaptationModel] = useState<string>(PROJECT_DEFAULT_AI_MODEL);
  const [questionModelTouched, setQuestionModelTouched] = useState(false);

  // ============================================================================
  // SPRINT 5: STATE MANAGEMENT (FR-014 to FR-018)
  // ============================================================================

  /** Internal selected task IDs state - used when externalSelectedTaskIds not provided */
  const [internalSelectedTaskIds, setInternalSelectedTaskIds] = useState<string[]>([]);

  // Use external selectedTaskIds if provided, otherwise use internal state (FR-017)
  const selectedTaskIds = externalSelectedTaskIds ?? internalSelectedTaskIds;

  /**
   * Handle task selection change from TasksTable
   * Updates internal state and notifies parent via callback
   */
  const handleTaskSelectionChange = useCallback((newSelectedIds: string[]) => {
    setInternalSelectedTaskIds(newSelectedIds);
    if (onTaskSelectionChange) {
      onTaskSelectionChange(newSelectedIds);
    }
  }, [onTaskSelectionChange]);

  // Computed: should footer toolbar be visible? (FR-014)
  const shouldShowFooter = tasks.length > 0;

  // Snackbar state for displaying notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Change detection for isDescriptionEffectivelySaved (FR-019)
  const changeDetection = useChangeDetection<ProductFormData>({
    ignoreFields: ['updatedAt', 'createdAt', '__typename', 'lastModified'],
    debug: process.env.NODE_ENV === 'development'
  });

  // Computed: is any operation running? (FR-018 - all buttons disabled when any operation is running)
  const isAnyOperationRunning = isSavingDescription || isFetchingLinkDescription || isUpdatingQuestions || isCreatingAudiences || isAdaptingQuestions;

  // Computed: is description effectively saved? (FR-019)
  const isDescriptionEffectivelySaved = !changeDetection.isDirty();

  // ProjectService instance (FR-006: ONLY use ProjectService, NOT direct Firebase SDK)
  const projectService = useMemo(() => new ProjectService(), []);

  // GuideService instance for contextual help
  const guideService = useMemo(() => new GuideService(), []);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initialize form data from project prop
  useEffect(() => {
    if (project) {
      const initialData: ProductFormData = {
        url: project.url || '',
        description: project.description || '',
        platform: project.platform || '',
        previousUserStepDescription: project.previousUserStepDescription || '',
        toneOfVoice: project.toneOfVoice || '',
        audienceModel: ((project as { audienceModel?: string }).audienceModel === 'b2b' ? 'b2b' : 'b2c'),
        designDescription: project.designDescription || '',
        category: project.category || '',
        trafficChannels: (project as any).trafficChannels || [],
        targetCountries: (project as any).targetCountries || [],
      };
      setFormData(initialData);
      changeDetection.startTracking(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]); // Only re-run when project changes, not on changeDetection change

  useEffect(() => {
    if (!updateQuestionsDialogOpen || questionModelTouched) return;
    setQuestionAdaptationModel(projectDefaultModel.value);
  }, [projectDefaultModel.value, questionModelTouched, updateQuestionsDialogOpen]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Show notification both in context and in local snackbar
   */
  const showLocalNotification = useCallback((message: string, severity: 'success' | 'error') => {
    // Update local snackbar state for immediate display
    setSnackbar({ open: true, message, severity });
    // Also add to context for global tracking
    showNotification(message, severity);
  }, [showNotification]);

  /**
   * Handle snackbar close
   */
  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  /**
   * Handle field change - update form data and change detection
   */
  const handleFieldChange = useCallback((field: keyof ProductFormData, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      changeDetection.updateCurrent(updated);
      return updated;
    });

    // Clear field-specific errors on change
    if (field === 'url') {
      setUrlError(null);
    }
    if (field === 'description') {
      setDescriptionError(null);
    }
  }, [changeDetection]);

  /**
   * Check if changes should be saved before performing gated operation (FR-021)
   */
  const checkSaveBeforeAction = useCallback((actionName: string): boolean => {
    if (!isDescriptionEffectivelySaved) {
      showLocalNotification(
        `Please save your changes before ${actionName}`,
        'error'
      );
      return false;
    }
    return true;
  }, [isDescriptionEffectivelySaved, showLocalNotification]);

  /**
   * Handle save button click (FR-005)
   * Validates inputs, calls ProjectService.update(), shows notifications
   */
  const handleSave = useCallback(async () => {
    if (!projectId || !currentUser) {
      showLocalNotification('Cannot save: Missing project or user information', 'error');
      return;
    }

    // Validate Product Link (FR-007)
    const urlValidationError = validateProductLink(formData.url);
    if (urlValidationError) {
      setUrlError(urlValidationError);
      return;
    }

    // Validate Product Description (FR-008)
    const descValidationError = validateProductDescription(formData.description);
    if (descValidationError) {
      setDescriptionError(descValidationError);
      return;
    }

    // Clear previous errors
    setUrlError(null);
    setDescriptionError(null);

    try {
      setIsSavingDescription(true);

      // Prepare update data with all 7 fields (FR-005)
      // Note: updatedAt is auto-injected by ProjectService.update()
      const updateData: Partial<Project> = {
        url: formData.url,
        description: formData.description,
        platform: formData.platform as 'web' | 'ios' | 'android',
        previousUserStepDescription: formData.previousUserStepDescription,
        toneOfVoice: formData.toneOfVoice,
        designDescription: formData.designDescription,
        category: formData.category,
        trafficChannels: formData.trafficChannels,
        targetCountries: formData.targetCountries,
        audienceModel: formData.audienceModel,
      } as any;

      // Call ProjectService.update() (FR-006: MANDATORY - uses setDoc with merge)
      await projectService.update(projectId, updateData);

      // Mark changes as saved (FR-019)
      changeDetection.acceptChanges();

      // Show success notification (FR-009)
      showLocalNotification('Product data saved successfully', 'success');

      // Call callback to notify parent (FR-022)
      if (onDescriptionAndSettingsSaved) {
        onDescriptionAndSettingsSaved();
      }

      // Refresh parent state (FR-024)
      if (fetchProjectData) {
        await fetchProjectData();
      }

    } catch (error) {
      // Show error notification with details (FR-010)
      const errorMessage = error instanceof Error ? error.message : 'Failed to save product data';
      showLocalNotification(errorMessage, 'error');
    } finally {
      setIsSavingDescription(false);
    }
  }, [
    projectId,
    currentUser,
    formData,
    projectService,
    changeDetection,
    showLocalNotification,
    onDescriptionAndSettingsSaved,
    fetchProjectData
  ]);

  /**
   * Handle Get Info from Link button click (FR-011)
   * Sends request to backend webhook which processes the URL
   * and updates project via Firestore (async operation)
   */
  const handleFetchFromLink = useCallback(async () => {
    // Validate URL before fetching (FR-013)
    if (!formData.url || formData.url.trim() === '') {
      setUrlError('Please enter a valid URL');
      return;
    }

    const urlValidationError = validateProductLink(formData.url);
    if (urlValidationError) {
      setUrlError(urlValidationError);
      return;
    }

    // Clear previous errors
    setUrlError(null);
    setIsFetchingLinkDescription(true);

    try {
      // This is a webhook-based API - sends request to backend
      // Backend processes URL and updates project via Firestore listener
      const result = await fetchProductDescriptionFromUrlWithProxy(
        formData.url,
        null, // reviewsLink
        (message) => console.log('Fetch progress:', message),
        undefined, // taskContext - not needed for this operation
        currentUser?.uid,
        projectId
      );

      if (result && result.status === 'payload_sent') {
        // Request sent successfully - backend will update project via Firestore
        showLocalNotification('Product information request sent! The description will be updated automatically.', 'success');
        // Optionally refresh project data
        if (fetchProjectData) {
          fetchProjectData();
        }
      } else {
        showLocalNotification('Failed to send product information request', 'error');
      }
    } catch (error) {
      // Show error notification with details
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch product description';
      showLocalNotification(errorMessage, 'error');
    } finally {
      setIsFetchingLinkDescription(false);
    }
  }, [formData.url, currentUser?.uid, projectId, fetchProjectData, showLocalNotification]);

  /**
   * Handle Create Audiences button click (FR-015, FR-016, FR-017)
   *
   * Opens CreateAudiencesDialog instead of directly creating audiences.
   * Dialog allows user to select tasks and set audience count before creating.
   */
  const handleCreateAudiencesClick = useCallback(() => {
    // Check if changes are saved first (FR-021)
    if (!checkSaveBeforeAction('creating audiences')) {
      return;
    }
    setCreateAudiencesDialogOpen(true);
  }, [checkSaveBeforeAction]);

  /**
   * Handle Create Audiences from dialog submission
   *
   * Called when user clicks "Create Audiences" in the dialog.
   * Uses the same API as the original handleCreateAudiencesClick.
   */
  const handleCreateAudiencesFromDialog = useCallback(async (
    dialogAudienceCount: number,
    dialogSelectedTaskIds: string[],
    useDeepResearch: boolean,
    model?: string,
  ) => {
    try {
      setIsCreatingAudiences(true);

      // Call the Sprint 5 callback if provided
      if (onCreateAudiences) {
        await onCreateAudiences(dialogAudienceCount, dialogSelectedTaskIds, useDeepResearch, model);
      }

      // Also call legacy callback for backwards compatibility
      if (onAudiencesGenerated) {
        onAudiencesGenerated();
      }

      showLocalNotification('Audiences created successfully', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create audiences';
      showLocalNotification(errorMessage, 'error');
      throw error; // Re-throw so dialog knows submission failed
    } finally {
      setIsCreatingAudiences(false);
    }
  }, [onCreateAudiences, onAudiencesGenerated, showLocalNotification]);

  /**
   * Handle Update Interview Questions button click (FR-015, FR-016)
   *
   * Opens the model-selection confirmation dialog before starting the agent.
   */
  const handleUpdateInterviewQuestionsClick = useCallback(() => {
    // Check if changes are saved first (FR-021)
    if (!checkSaveBeforeAction('updating interview questions')) {
      return;
    }

    setQuestionModelTouched(false);
    setQuestionAdaptationModel(projectDefaultModel.value);
    setUpdateQuestionsDialogOpen(true);
  }, [checkSaveBeforeAction, projectDefaultModel.value]);

  const handleQuestionModelChange = useCallback((model: string) => {
    setQuestionModelTouched(true);
    setQuestionAdaptationModel(model);
  }, []);

  /**
   * Handle Update Interview Questions dialog submission (FR-015, FR-016)
   *
   * FR-016: Shows loading spinner during operation
   * FR-018: All buttons disabled when this operation is running
   */
  const handleUpdateInterviewQuestionsFromDialog = useCallback(async () => {
    try {
      setIsUpdatingQuestions(true);

      if (onUpdateInterviewQuestions) {
        await onUpdateInterviewQuestions(questionAdaptationModel);
      }

      setUpdateQuestionsDialogOpen(false);
      showLocalNotification('Interview questions updated successfully', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update interview questions';
      showLocalNotification(errorMessage, 'error');
    } finally {
      setIsUpdatingQuestions(false);
    }
  }, [onUpdateInterviewQuestions, questionAdaptationModel, showLocalNotification]);

  /**
   * Handle Adapt Questions button click — AI adapts questions to product context
   */
  const handleAdaptQuestionsClick = useCallback(async () => {
    if (!checkSaveBeforeAction('adapting interview questions')) {
      return;
    }

    try {
      setIsAdaptingQuestions(true);

      if (onAdaptQuestions) {
        await onAdaptQuestions();
      }

      showLocalNotification('Interview questions adaptation started', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to adapt interview questions';
      showLocalNotification(errorMessage, 'error');
    } finally {
      setIsAdaptingQuestions(false);
    }
  }, [checkSaveBeforeAction, onAdaptQuestions, showLocalNotification]);

  /**
   * Handle delete selected tasks from TasksTable toolbar
   *
   * This is passed to TasksTable which shows a selection toolbar with Delete button
   */
  const handleDeleteSelectedTasks = useCallback(async (taskIdsToDelete: string[]) => {
    if (!onDeleteTasks) return;

    try {
      await onDeleteTasks(taskIdsToDelete);

      // Clear selection after successful deletion
      setInternalSelectedTaskIds([]);
      if (onTaskSelectionChange) {
        onTaskSelectionChange([]);
      }

      showLocalNotification('Tasks deleted successfully', 'success');

      // Refresh parent state after deletion
      if (fetchProjectData) {
        await fetchProjectData();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete tasks';
      showLocalNotification(errorMessage, 'error');
      throw error; // Re-throw so TasksTable knows deletion failed
    }
  }, [onDeleteTasks, onTaskSelectionChange, showLocalNotification, fetchProjectData]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Stack spacing={theme.spacing(3)} sx={{ width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column', backgroundColor: theme.palette.background.paper, p: { xs: 1, sm: 2, md: 3 }, borderRadius: theme.shape.borderRadius }}>
      {/* Section: AI Product Research */}
      <SAccordion
        title="AI Product Research"
        collapsible
        defaultCollapsed={false}
      >
        {reviewAnalysisId ? (
          <ScrapeReviewPanel
            projectId={projectId!}
            userId={currentUser?.uid || ''}
            analysisId={reviewAnalysisId}
            onApplied={() => {
              setReviewAnalysisId(null);
              fetchProjectData?.();
            }}
            onClose={() => setReviewAnalysisId(null)}
          />
        ) : (
          <ScrapeAnalyzeSection
            projectId={projectId!}
            userId={currentUser?.uid || ''}
            onComplete={() => fetchProjectData?.()}
            onReviewReady={(analysisId) => setReviewAnalysisId(analysisId)}
            onOpenHistory={() => setShowResearchHistory(true)}
          />
        )}
      </SAccordion>

      {/* Research History Dialog (popup) */}
      <ResearchHistoryDialog
        open={showResearchHistory}
        onClose={() => setShowResearchHistory(false)}
        projectId={projectId!}
        userId={currentUser?.uid || ''}
        onApplied={() => { setShowResearchHistory(false); fetchProjectData?.(); }}
      />

      {/* Product Description */}
      <StyledTextArea
        label="Product Description"
        value={formData.description}
        onChange={(value) => handleFieldChange('description', value)}
        errorMessage={descriptionError || undefined}
        rows={6}
        required
        fullWidth
      />

      {/* Section: Product Info - collapsible, collapsed by default */}
      <SAccordion
        title="Product Info"
        collapsible
        defaultCollapsed={true}
      >
        <Stack spacing={theme.spacing(2)}>
          {/* Platform - required, dropdown (FR-001) */}
          <StyledSelectDropdown
            label="Platform"
            value={formData.platform}
            onChange={(value) => handleFieldChange('platform', String(value))}
            options={PLATFORM_OPTIONS}
            required
            fullWidth
          />

          {/* Audience Model - B2C/B2B toggle. Drives audience/actor/interview prompts. */}
          <StyledSelectDropdown
            label="Audience Model"
            value={formData.audienceModel}
            onChange={(value) => handleFieldChange('audienceModel', String(value) === 'b2b' ? 'b2b' : 'b2c')}
            options={AUDIENCE_MODEL_OPTIONS}
            fullWidth
          />

          {/* Category - optional, single-line (FR-001) */}
          <StyledTextField
            label="Category"
            value={formData.category}
            onChange={(value) => handleFieldChange('category', value)}
            fullWidth
          />

          {/* Target Countries - multi-select */}
          <Autocomplete<CountryOption, true>
            multiple
            options={COUNTRY_OPTIONS.filter(o => !formData.targetCountries.includes(o.code))}
            getOptionLabel={(option) => `${option.label} (${option.code})`}
            value={COUNTRY_OPTIONS.filter(o => formData.targetCountries.includes(o.code))}
            onChange={(_e, newValue) => {
              setFormData(prev => ({ ...prev, targetCountries: newValue.map(v => v.code) }));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={`${option.label} (${option.code})`}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.code}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Target Countries" placeholder="Add country..." size="small" />
            )}
            isOptionEqualToValue={(option, value) => option.code === value.code}
          />

          {/* Traffic Channels - percentage distribution */}
          <TrafficChannelsEditor
            channels={formData.trafficChannels}
            onChange={(channels) => setFormData(prev => ({ ...prev, trafficChannels: channels }))}
          />
        </Stack>
      </SAccordion>

      {/* Section: Design Information - collapsible, collapsed by default (FR-004) */}
      <SAccordion
        title="Design Information"
        collapsible
        defaultCollapsed={true}
      >
        <Stack spacing={theme.spacing(2)}>
          {/* Previous User Step Description - TEMPORARILY HIDDEN (FR-001)
          <StyledTextArea
            label="Previous User Step Description"
            value={formData.previousUserStepDescription}
            onChange={(value) => handleFieldChange('previousUserStepDescription', value)}
            rows={2}
            fullWidth
          />
          */}

          {/* Tone of Voice - optional, single-line (FR-001) */}
          <StyledTextField
            label="Tone of Voice"
            value={formData.toneOfVoice}
            onChange={(value) => handleFieldChange('toneOfVoice', value)}
            fullWidth
          />

          {/* Design Description - optional, 3 rows (FR-001) */}
          <StyledTextArea
            label="Design Description"
            value={formData.designDescription}
            onChange={(value) => handleFieldChange('designDescription', value)}
            rows={3}
            fullWidth
          />

          {/* Theme V2 — inline content, nesting context reset for dialogs containing SAccordion */}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Theme</Typography>
          <SAccordionNestingContext.Provider value={false}>
            <ProjectThemeSection
              activeTheme={projectThemes.activeTheme}
              projectId={projectId}
              themes={projectThemes.themes}
              loading={projectThemes.loading}
              onAddTheme={projectThemes.addTheme}
              onRemoveTheme={projectThemes.removeTheme}
              onUpdateTheme={projectThemes.updateTheme}
              onCreateGeneratedTheme={projectThemes.createCandidate}
              onSetActiveTheme={projectThemes.setActive}
              inline
            />
          </SAccordionNestingContext.Provider>

        </Stack>
      </SAccordion>


      {/* ============================================================================
         VARIABLES: Project-level variable definitions
         Replaces Task.requiredInfo with structured FunnelVariable definitions.
         Variables can be scoped to specific tasks via taskIds[].
         ============================================================================ */}
      <Divider sx={{ my: theme.spacing(2) }} />
      <SAccordion
        title="Variables"
        collapsible
        defaultCollapsed
        data-testid={PRODUCT_VARIABLES.accordion}
      >
        <VariableSetEditor
          variables={variables}
          onChange={handleVariablesChange}
          tasks={(tasks ?? []).map(t => ({ id: t.id!, name: t.name ?? t.id! }))}
          disabled={isAnyOperationRunning}
          onSaveVersion={onSaveVariableVersion}
          currentVersion={currentVariableVersion}
        />
      </SAccordion>

      {/* ============================================================================
         SPRINT 5: Tasks Section with TasksTable (FR-014)
         Always render TasksTable - it has its own empty state with create buttons
         ============================================================================ */}
      <Divider sx={{ my: theme.spacing(2) }} />

      <TasksTable
        tasks={tasks}
        onSelectionChange={handleTaskSelectionChange}
        onCreateTasksFromDescription={onCreateTasksFromDescription}
        onCreateTask={onCreateTask}
        onUpdateTask={onUpdateTask}
        onDeleteTask={onDeleteTask}
        onDuplicateTask={onDuplicateTask}
        onGenerateAudience={onGenerateAudience}
        onDeleteSelectedTasks={handleDeleteSelectedTasks}
        isOperationRunning={isAnyOperationRunning}
        isDescriptionSaved={isDescriptionEffectivelySaved}
        variables={variables}
        onVariablesChange={handleVariablesChange}
      />

      {/* ============================================================================
         SPRINT 5: Footer Toolbar (FR-014, FR-015, FR-016, FR-017, FR-018)
         Footer appears when tasks.length > 0
         Simplified: Removed Audience Count (now in dialog), Create Onboarding, Delete (moved to TasksTable toolbar)
         ============================================================================ */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'none',
          backdropFilter: 'blur(34px)',
          borderRadius: theme.shape.borderRadius,
          boxShadow: 'none',
          padding: theme.spacing(2),
          zIndex: theme.zIndex.appBar,
          marginTop: 'auto',
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {/* Create Audiences button */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleCreateAudiencesClick}
            disabled={tasks.length === 0 || isAnyOperationRunning}
            data-testid={AUDIENCE_GENERATION.openButton}
          >
            {isCreatingAudiences ? <CircularProgress size={20} /> : 'Create Audiences'}
          </Button>

          {/* Update Interview Questions button */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleUpdateInterviewQuestionsClick}
            disabled={isAnyOperationRunning}
          >
            {isUpdatingQuestions ? <CircularProgress size={20} /> : 'Update Interview Questions'}
          </Button>

          {/* View Questions button — admin-side read-only view of currently saved
              adopted questions. Disabled when nothing has been adapted yet. */}
          <Button
            variant="text"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => { setViewQuestionsTab(0); setViewQuestionsOpen(true); }}
            disabled={(() => {
              const p = project as null | undefined | {
                adoptedQuestionsBlocks?: Record<string, string>;
                adoptedQuestions?: string;
                interviewQuestions?: string;
              };
              return !p?.adoptedQuestionsBlocks && !p?.adoptedQuestions && !p?.interviewQuestions;
            })()}
          >
            View Questions
          </Button>

          {/* Save button */}
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={isAnyOperationRunning}
          >
            {isSavingDescription ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Stack>
      </Box>


      {/* Unsaved changes indicator */}
      {!isDescriptionEffectivelySaved && (
        <Alert severity="info" sx={{ mt: theme.spacing(1) }}>
          You have unsaved changes
        </Alert>
      )}

      {/* Notification Snackbar (FR-009, FR-010) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Create Audiences Dialog */}
      <CreateAudiencesDialog
        open={createAudiencesDialogOpen}
        tasks={tasks}
        defaultAudienceCount={AUDIENCE_COUNT_DEFAULT}
        projectId={projectId}
        audienceModel={(project as { audienceModel?: 'b2b' | 'b2c' } | null | undefined)?.audienceModel === 'b2b' ? 'b2b' : 'b2c'}
        onClose={() => setCreateAudiencesDialogOpen(false)}
        onCreateAudiences={handleCreateAudiencesFromDialog}
      />

      <SDialog
        open={updateQuestionsDialogOpen}
        onClose={() => {
          if (!isUpdatingQuestions) setUpdateQuestionsDialogOpen(false);
        }}
        title="Update Interview Questions"
        maxWidth="sm"
        fullWidth
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setUpdateQuestionsDialogOpen(false),
          disabled: isUpdatingQuestions,
        }}
        primaryAction={{
          label: isUpdatingQuestions ? 'Updating...' : 'Update Questions',
          onClick: handleUpdateInterviewQuestionsFromDialog,
          disabled: isUpdatingQuestions,
          loading: isUpdatingQuestions,
          'data-testid': 'question-adaptation-submit-button',
        }}
        showCloseButton={false}
        data-testid="question-adaptation-model-dialog"
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Select the AI model that will adapt the interview questions to this product context.
          </Typography>
          <AvailableModelSelector
            projectId={projectId}
            value={questionAdaptationModel}
            onChange={handleQuestionModelChange}
            label="AI Model"
            size="small"
            fullWidth
            disabled={isUpdatingQuestions}
            testId="question-adaptation-model-selector"
          />
        </Stack>
      </SDialog>

      {/* View Adopted Questions — admin / debug read-only view of what
          AdaptQuestionsHandler last produced for this project. Branches by
          adoptedQuestionsMode ('role-blocks' = B2B with 7-key tabbed view;
          'flat' or undefined = single pre-block). Always reads from the live
          project prop, so no manual refresh needed after Update Interview
          Questions completes. */}
      {(() => {
        // The codebase's `Project` type doesn't include adoptedQuestions /
        // adoptedQuestionsMode / adoptedQuestionsBlocks yet (legacy schema),
        // so cast through a narrow shape that adds the fields we need.
        const projectAny = project as null | undefined | {
          adoptedQuestionsMode?: 'flat' | 'role-blocks';
          adoptedQuestionsBlocks?: Record<string, string>;
          adoptedQuestions?: string;
          interviewQuestions?: string;
        };
        const blocks = projectAny?.adoptedQuestionsBlocks;
        const mode = projectAny?.adoptedQuestionsMode
          ?? (blocks && typeof blocks === 'object' ? 'role-blocks' : 'flat');
        const flatText = projectAny?.adoptedQuestions ?? projectAny?.interviewQuestions ?? '';
        const blockKeys = blocks ? Object.keys(blocks) : [];
        const safeTab = blockKeys.length > 0 ? Math.min(viewQuestionsTab, blockKeys.length - 1) : 0;
        const activeKey = blockKeys[safeTab];
        const activeContent = activeKey && blocks ? blocks[activeKey] : '';
        const charCount = mode === 'role-blocks' && blocks
          ? Object.values(blocks).reduce((s, v) => s + (v?.length ?? 0), 0)
          : flatText.length;

        return (
          <Dialog
            open={viewQuestionsOpen}
            onClose={() => setViewQuestionsOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Adopted Interview Questions
                <Chip
                  size="small"
                  label={mode === 'role-blocks' ? `B2B · ${blockKeys.length} blocks · ${charCount.toLocaleString()} chars` : `flat · ${charCount.toLocaleString()} chars`}
                />
              </Box>
              <IconButton size="small" onClick={() => setViewQuestionsOpen(false)} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {mode === 'role-blocks' && blocks && blockKeys.length > 0 ? (
                <>
                  <Tabs
                    value={safeTab}
                    onChange={(_, v: number) => setViewQuestionsTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                  >
                    {blockKeys.map((k) => (
                      <Tab key={k} label={`${k} (${(blocks[k]?.length ?? 0).toLocaleString()})`} />
                    ))}
                  </Tabs>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      maxHeight: '60vh',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                      fontSize: 13,
                      lineHeight: 1.5,
                      bgcolor: 'background.default',
                    }}
                  >
                    {activeContent || '(empty)'}
                  </Paper>
                </>
              ) : flatText ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: '60vh',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    fontSize: 13,
                    lineHeight: 1.5,
                    bgcolor: 'background.default',
                  }}
                >
                  {flatText}
                </Paper>
              ) : (
                <Alert severity="info">
                  No adopted questions saved for this project yet. Click <strong>Update Interview Questions</strong> to generate them.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              {mode === 'role-blocks' && blocks && activeContent && (
                <Button
                  size="small"
                  onClick={() => navigator.clipboard?.writeText(activeContent)}
                >
                  Copy active block
                </Button>
              )}
              {mode === 'flat' && flatText && (
                <Button
                  size="small"
                  onClick={() => navigator.clipboard?.writeText(flatText)}
                >
                  Copy
                </Button>
              )}
              <Button onClick={() => setViewQuestionsOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Stack>
  );
};
